import React from 'react';
import { Badge } from '../UI/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../UI/table';
import { useLocale } from '../../contexts/LocaleContext';
import { riskAnalysisService } from '../../services/riskAnalysisService';

const formatScore = (value) => {
  if (value == null || Number.isNaN(Number(value))) {
    return '—';
  }
  return Number(value).toFixed(1);
};

function RiskScoreBadge({ score }) {
  if (score == null) {
    return <span className="text-text-tertiary">—</span>;
  }
  const colors = riskAnalysisService.getRiskScoreColor(Number(score));
  return (
    <Badge variant="outline" className={`${colors.text} ${colors.bg} ${colors.border}`}>
      {formatScore(score)}
    </Badge>
  );
}

function TenantRiskBreakdownSections({ analysis }) {
  const { t } = useLocale();

  if (!analysis) {
    return null;
  }

  const {
    tenantOverallRiskScore,
    supplierBreakdown,
    pathImpacts,
    geopoliticalBreakdown,
    contributingLogisticsNodes,
    notes,
  } = analysis;

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-wrap items-center gap-4 rounded-lg border border-border-light bg-background-secondary p-4">
        <div>
          <p className="text-sm text-text-secondary">{t('tenantGraphAnalysisOverallScore')}</p>
          <div className="mt-1 text-3xl font-semibold tabular-nums text-text-primary">
            <RiskScoreBadge score={tenantOverallRiskScore} />
          </div>
        </div>
      </div>

      {Array.isArray(notes) && notes.length > 0 ? (
        <div className="rounded-lg border border-warning-200 bg-warning-50 px-4 py-3 text-sm text-warning-800">
          <p className="font-medium">{t('tenantGraphAnalysisNotes')}</p>
          <ul className="mt-2 list-disc space-y-1 pl-5">
            {notes.map((note, index) => (
              <li key={`note-${index}`}>{note}</li>
            ))}
          </ul>
        </div>
      ) : null}

      {Array.isArray(supplierBreakdown) && supplierBreakdown.length > 0 ? (
        <section>
          <h3 className="mb-3 text-sm font-semibold text-text-primary">
            {t('tenantGraphAnalysisSupplierBreakdown')}
          </h3>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('tenantGraphAnalysisColSupplier')}</TableHead>
                <TableHead>{t('tenantGraphAnalysisColWeighted')}</TableHead>
                <TableHead>{t('tenantGraphAnalysisColNews')}</TableHead>
                <TableHead>{t('tenantGraphAnalysisColPath')}</TableHead>
                <TableHead>{t('tenantGraphAnalysisColGeo')}</TableHead>
                <TableHead>{t('tenantGraphAnalysisColLogistics')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {supplierBreakdown.map((row) => (
                <TableRow key={row.ticker || row.supplierId || row.supplierName}>
                  <TableCell className="font-medium">
                    {row.supplierName || row.ticker || row.supplierId}
                  </TableCell>
                  <TableCell>
                    <RiskScoreBadge score={row.weightedRiskScore} />
                  </TableCell>
                  <TableCell>{formatScore(row.newsRiskScore)}</TableCell>
                  <TableCell>{formatScore(row.pathExposureScore)}</TableCell>
                  <TableCell>{formatScore(row.geoPoliticalScore)}</TableCell>
                  <TableCell>{formatScore(row.logisticsScore)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </section>
      ) : null}

      {Array.isArray(pathImpacts) && pathImpacts.length > 0 ? (
        <section>
          <h3 className="mb-3 text-sm font-semibold text-text-primary">
            {t('tenantGraphAnalysisPathImpacts')}
          </h3>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('tenantGraphAnalysisColRoute')}</TableHead>
                <TableHead>{t('tenantGraphAnalysisColImpact')}</TableHead>
                <TableHead>{t('tenantGraphAnalysisColExplanation')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {pathImpacts.map((row) => (
                <TableRow
                  key={
                    row.routeId ||
                    `${row.ticker || row.supplierId}-${row.startTicker || row.startCompanyId}`
                  }
                >
                  <TableCell>
                    {row.routeId ||
                      [row.startTicker || row.startCompanyId, row.endTicker || row.endCompanyId]
                        .filter(Boolean)
                        .join(' → ') ||
                      '—'}
                  </TableCell>
                  <TableCell>
                    <RiskScoreBadge score={row.impactScore} />
                  </TableCell>
                  <TableCell className="max-w-md text-text-secondary">{row.explanation || '—'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </section>
      ) : null}

      {Array.isArray(geopoliticalBreakdown) && geopoliticalBreakdown.length > 0 ? (
        <section>
          <h3 className="mb-3 text-sm font-semibold text-text-primary">
            {t('tenantGraphAnalysisGeopolitical')}
          </h3>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('tenantGraphAnalysisColRegion')}</TableHead>
                <TableHead>{t('tenantGraphAnalysisColScore')}</TableHead>
                <TableHead>{t('tenantGraphAnalysisColReason')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {geopoliticalBreakdown.map((row) => (
                <TableRow key={row.geographyId || row.isoCode || row.name}>
                  <TableCell>
                    {[row.name, row.isoCode].filter(Boolean).join(' ') || row.geographyId}
                  </TableCell>
                  <TableCell>
                    <RiskScoreBadge score={row.score} />
                  </TableCell>
                  <TableCell className="text-text-secondary">{row.reason || '—'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </section>
      ) : null}

      {Array.isArray(contributingLogisticsNodes) && contributingLogisticsNodes.length > 0 ? (
        <section>
          <h3 className="mb-3 text-sm font-semibold text-text-primary">
            {t('tenantGraphAnalysisLogistics')}
          </h3>
          <ul className="list-disc space-y-1 pl-5 text-sm text-text-secondary">
            {contributingLogisticsNodes.map((nodeId, index) => (
              <li key={`logistics-${nodeId}-${index}`}>{nodeId}</li>
            ))}
          </ul>
        </section>
      ) : null}
    </div>
  );
}

export default TenantRiskBreakdownSections;
